"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { Grid, Section } from "@/ui";
import type { GiftSet } from "../../shared/types";
import { GiftSetCard } from "./GiftSetCard";

interface GiftSetsSectionProps {
  giftSets: GiftSet[];
}

export const GiftSetsSection = ({ giftSets }: GiftSetsSectionProps) => {
  const locale = useLocale();
  const giftT = useTranslations("gift_sets");

  // Hide section if no gift sets
  if (!giftSets || giftSets.length === 0) {
    return null;
  }

  return (
    <Section
      id="gift-sets"
      data-testid="gift-sets-section"
      className="section-spacing"
    >
      {/* Section Header */}
      <div className="mb-10 flex items-end justify-between gap-4">
        <div className="max-w-2xl">
          <span className="section-label block mb-3">{giftT("label")}</span>
          <h2 className="heading-display">{giftT("title")}</h2>
          <p className="heading-sub mt-3 max-w-xl">{giftT("subtitle")}</p>
        </div>
        <Link
          href={`/${locale}/gift-sets`}
          className="hidden shrink-0 text-sm font-medium text-muted-foreground underline-offset-4 transition-all hover:text-foreground hover:underline sm:block"
        >
          {giftT("cta_shop_all")}
        </Link>
      </div>

      {/* Gift sets grid — card density aligned with storefront product grids */}
      <Grid className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4">
        {giftSets.map((giftSet, index) => (
          <GiftSetCard
            key={giftSet.id}
            giftSet={giftSet}
            index={index}
          />
        ))}
      </Grid>

      {/* Mobile view all link */}
      <div className="mt-6 text-center sm:hidden">
        <Link
          href={`/${locale}/gift-sets`}
          className="text-sm font-medium text-muted-foreground underline-offset-4 transition-all hover:text-foreground hover:underline"
        >
          {giftT("cta_shop_all")}
        </Link>
      </div>
    </Section>
  );
};
